import { useEffect, useState } from 'react';
import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';

/** Retain successful reads on transient failure; evict privileged data on access loss. */
export function useAdminQuery<T>(queryKey: QueryKey, read: () => Promise<T>, enabled = true) {
  const client = useQueryClient();
  const [deniedKey, setDeniedKey] = useState<string | null>(null);
  const keyHash = JSON.stringify(queryKey);
  const accessDenied = deniedKey === keyHash;
  const query = useQuery({ queryKey, enabled: enabled && !accessDenied, staleTime: 0, gcTime: 0,
    retry: false,
    queryFn: async () => {
      try { return await read(); } catch (error) {
        const status = (error as { statusCode?: number })?.statusCode;
        if (status === 401 || status === 403) setDeniedKey(keyHash);
        throw error;
      }
    },
  });
  useEffect(() => {
    if (accessDenied) client.removeQueries({ queryKey: JSON.parse(keyHash), exact: true });
  }, [accessDenied, client, keyHash]);
  return { ...query, data: accessDenied ? undefined : query.data, isError: accessDenied || query.isError, accessDenied };
}
